// ============================================================
// INVOICE SUBMIT MODAL
// Submit invoice with amount, validates against approved quote
// ============================================================

import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Receipt, Loader2, AlertTriangle, Check } from 'lucide-react';
import { runJobAction, LHS_API } from '../utils/lhsCore';

export default function InvoiceSubmitModal({ 
  job, 
  isOpen,
  onClose, 
  onJobUpdated,
  onError,
}) {
  const [invoiceNumber, setInvoiceNumber] = useState('');
  const [amount, setAmount] = useState(job?.approved_amount?.toString() || '');
  const [loading, setLoading] = useState(false);

  if (!isOpen || !job) return null;

  const approvedAmount = job.approved_amount || 0;
  const enteredAmount = parseFloat(amount) || 0;
  const variance = approvedAmount > 0 ? ((enteredAmount - approvedAmount) / approvedAmount) : 0;
  const variancePercent = (variance * 100).toFixed(1);

  const hasOverQuoteWarning = variance > 0.10;
  const hasCriticalVariance = variance > 0.25;

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-GB', {
      style: 'currency',
      currency: 'GBP',
    }).format(value || 0);
  };

  const handleSubmit = async () => {
    if (!amount || parseFloat(amount) <= 0) return;

    setLoading(true);
    try {
      const updatedJob = await runJobAction(
        LHS_API.submitInvoice(job.id),
        { 
          amount: parseFloat(amount),
          invoice_number: invoiceNumber || undefined,
        }
      );

      if (onJobUpdated) {
        onJobUpdated(updatedJob);
      }

      onClose();
    } catch (error) {
      if (onError) {
        onError(error.message);
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4"
        onClick={onClose}
      >
        <motion.div
          initial={{ opacity: 0, scale: 0.95, y: 20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.95, y: 20 }}
          onClick={(e) => e.stopPropagation()}
          className="bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden"
        >
          {/* Header */}
          <div className="bg-gradient-to-r from-teal-600 to-teal-700 p-5">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-white/20 flex items-center justify-center">
                  <Receipt className="w-5 h-5 text-white" />
                </div>
                <div>
                  <h2 className="text-lg font-semibold text-white">Submit Invoice</h2>
                  <p className="text-sm text-white/70">{job.job_ref} - PO: {job.po_number}</p>
                </div>
              </div>
              <button
                onClick={onClose}
                className="w-8 h-8 rounded-lg bg-white/10 hover:bg-white/20 flex items-center justify-center transition-colors"
              >
                <X className="w-5 h-5 text-white" />
              </button>
            </div>
          </div>

          {/* Content */}
          <div className="p-5 space-y-4">
            {/* Approved Amount Reference */}
            <div className="bg-gray-50 rounded-xl p-4">
              <div className="text-sm text-gray-500 mb-1">Approved Quote Amount</div>
              <div className="text-2xl font-bold text-gray-900">
                {formatCurrency(approvedAmount)}
              </div>
            </div>

            {/* Invoice Number */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Invoice Number (Optional)
              </label>
              <input
                type="text"
                value={invoiceNumber}
                onChange={(e) => setInvoiceNumber(e.target.value)}
                placeholder="e.g., INV-2024-001"
                className="w-full px-4 py-3 rounded-xl border border-gray-300 focus:border-teal-500 
                           focus:ring-2 focus:ring-teal-500/20 outline-none transition-all"
                data-testid="invoice-number-input"
              />
            </div>

            {/* Invoice Amount */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Invoice Amount *
              </label>
              <div className="relative">
                <span className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-500 font-medium">
                  £
                </span>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  value={amount}
                  onChange={(e) => setAmount(e.target.value)}
                  placeholder="0.00"
                  className="w-full pl-8 pr-4 py-3 rounded-xl border border-gray-300 focus:border-teal-500 
                             focus:ring-2 focus:ring-teal-500/20 outline-none transition-all text-lg font-medium"
                  data-testid="invoice-amount-input"
                />
              </div>
            </div>

            {/* Variance Warning */}
            {enteredAmount > 0 && variance !== 0 && (
              <motion.div
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                className={`rounded-xl p-4 ${
                  hasCriticalVariance 
                    ? 'bg-red-50 border border-red-200' 
                    : hasOverQuoteWarning 
                      ? 'bg-yellow-50 border border-yellow-200'
                      : 'bg-green-50 border border-green-200'
                }`}
              >
                <div className="flex items-start gap-3">
                  {hasCriticalVariance ? (
                    <AlertTriangle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
                  ) : hasOverQuoteWarning ? (
                    <AlertTriangle className="w-5 h-5 text-yellow-500 flex-shrink-0 mt-0.5" />
                  ) : (
                    <Check className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
                  )}
                  <div>
                    <div className={`font-medium ${
                      hasCriticalVariance ? 'text-red-800' : hasOverQuoteWarning ? 'text-yellow-800' : 'text-green-800'
                    }`}>
                      {variance > 0 ? '+' : ''}{variancePercent}% variance
                    </div>
                    <div className={`text-sm ${
                      hasCriticalVariance ? 'text-red-600' : hasOverQuoteWarning ? 'text-yellow-600' : 'text-green-600'
                    }`}>
                      {hasCriticalVariance && 'Critical variance - requires management approval to close'}
                      {hasOverQuoteWarning && !hasCriticalVariance && 'Over quote - will be flagged for review'}
                      {!hasOverQuoteWarning && 'Within acceptable range'}
                    </div>
                  </div>
                </div>
              </motion.div>
            )}
          </div>

          {/* Footer */}
          <div className="p-5 bg-gray-50 border-t">
            <motion.button
              whileHover={{ scale: 1.01 }}
              whileTap={{ scale: 0.99 }}
              disabled={!amount || parseFloat(amount) <= 0 || loading}
              onClick={handleSubmit}
              data-testid="submit-invoice-btn"
              className="w-full py-3.5 rounded-xl bg-teal-600 hover:bg-teal-700 text-white font-semibold 
                         disabled:opacity-40 disabled:cursor-not-allowed transition-all
                         flex items-center justify-center gap-2"
            >
              {loading ? (
                <>
                  <Loader2 className="w-5 h-5 animate-spin" />
                  Submitting...
                </>
              ) : (
                <>
                  <Receipt className="w-5 h-5" />
                  Submit Invoice
                </>
              )}
            </motion.button>

            <button
              onClick={onClose}
              className="mt-2 w-full py-2.5 rounded-xl border border-gray-300 text-gray-600 
                         hover:bg-gray-100 transition-colors font-medium"
            >
              Cancel
            </button>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}
